import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { resolveBaseUrl } from "@/lib/portalApi";
import SiteLink from "@/components/SiteLink";

export const Route = createFileRoute("/portal_/auth/verify-email")({
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError("No verification token provided");
      setLoading(false);
      return;
    }

    fetch(`${resolveBaseUrl()}/api/v1/auth/verify-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) {
          setError(data.detail || "Verification failed");
        } else {
          setSuccess(true);
          setMessage(data.message || "Your email has been verified.");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Could not reach the server. Please try again.");
        setLoading(false);
      });
  }, [token]);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-10 sm:px-6">
        <SiteLink
          href="/"
          className="inline-flex items-center gap-4 rounded-3xl border border-slate-200 bg-white px-4 py-2 shadow-sm transition hover:border-slate-300"
          style={{ width: "fit-content" }}
        >
          <img
            src="/Larasoft Logo-landscape.svg"
            alt="Larasoft"
            width={180}
            height={64}
            className="h-12"
            style={{ width: "auto" }}
          />
          <span className="rounded-full bg-brand-blue-light/20 px-3 py-1 text-xs font-bold uppercase tracking-[0.3em] text-brand-blue-dark">
            Client portal
          </span>
        </SiteLink>

        <div className="mt-12 flex justify-center">
          <Card className="w-full max-w-md">
            {loading ? (
              <CardContent className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </CardContent>
            ) : success ? (
              <>
                <CardHeader className="text-center">
                  <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
                  <CardTitle className="mt-4">Email Verified</CardTitle>
                  <CardDescription>{message}</CardDescription>
                  <CardDescription>
                    Sign in to get started.
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <SiteLink href="/portal/sign-in">
                    <Button>Sign In</Button>
                  </SiteLink>
                </CardContent>
              </>
            ) : (
              <>
                <CardHeader className="text-center">
                  <XCircle className="mx-auto h-12 w-12 text-destructive" />
                  <CardTitle className="mt-4">Verification Failed</CardTitle>
                  <CardDescription>{error}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <SiteLink href="/portal/sign-in">
                    <Button variant="outline">Go to Sign In</Button>
                  </SiteLink>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
